import { supabase } from './supabase';
import { AuthError } from '@supabase/supabase-js';

export class SupabaseErrorHandler {
  private static retryCount = 0;
  private static maxRetries = 3;
  private static retryDelay = 1000; // 1 second

  static async withRetry<T>(
    operation: () => Promise<T>,
    context: string = 'Supabase operation'
  ): Promise<T> {
    for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
      try {
        const result = await operation();
        this.retryCount = 0; // Reset on success
        return result;
      } catch (error) {
        console.error(`${context} failed (attempt ${attempt + 1}):`, error);

        if (attempt === this.maxRetries) {
          // Last attempt failed
          if (error instanceof Error) {
            if (error.message.includes('Failed to fetch') || 
                error.message.includes('Network error') ||
                error.message.includes('Request timeout')) {
              throw new Error(
                'Unable to connect to the server. Please check your internet connection and try again.'
              );
            }
            if (error.message.includes('Invalid API key') || 
                error.message.includes('Unauthorized')) {
              throw new Error(
                'Authentication configuration error. Please contact support.'
              );
            }
          }
          throw error;
        }

        // Wait before retry
        await new Promise(resolve => setTimeout(resolve, this.retryDelay * (attempt + 1)));
      }
    }

    throw new Error(`${context} failed after ${this.maxRetries} attempts`);
  }

  static async getSession() {
    return this.withRetry(
      async () => {
        const { data, error } = await supabase.auth.getSession();
        if (error) throw error;
        return data;
      },
      'Get session'
    );
  }

  static async getUser() {
    return this.withRetry(
      async () => {
        const { data, error } = await supabase.auth.getUser();
        if (error) throw error;
        return data;
      },
      'Get user'
    );
  }

  static async signOut() {
    return this.withRetry(
      async () => {
        const { error } = await supabase.auth.signOut();
        if (error) throw error;
      },
      'Sign out'
    );
  }

  static async signInWithPassword(email: string, password: string) {
    return this.withRetry(
      async () => {
        const { data, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        return data;
      },
      'Sign in with password'
    );
  }

  static async signUp(email: string, password: string) {
    return this.withRetry(
      async () => {
        const { data, error } = await supabase.auth.signUp({
          email,
          password,
        });
        if (error) throw error;
        return data;
      },
      'Sign up'
    );
  }
}
